import { App, notification as staticNotification } from 'antd';
import type { ArgsProps } from 'antd/es/notification';
import type { ApiError } from '../types/api.types';

/**
 * Notification System Utility
 * 
 * This module provides a comprehensive notification wrapper around Ant Design's notification API.
 * It implements Requirement 24 for visual feedback on all user actions.
 * 
 * Features:
 * - Success notifications (3 seconds duration)
 * - Error notifications (5 seconds duration)
 * - Info and warning notifications (4 seconds duration)
 * - Network error notifications with retry button
 * - Consistent positioning (top-right corner)
 * - Type-safe notification options
 * 
 * Usage Examples:
 * 
 * 1. Show success notification:
 *    notifySuccess('Student created successfully');
 *    notifySuccess('Payment recorded', 'Receipt #12345 generated');
 * 
 * 2. Show error notification:
 *    notifyError('Failed to save data');
 *    notifyError(apiError, 'Custom error message');
 * 
 * 3. Show network error with retry:
 *    notifyNetworkError(() => retryApiCall());
 * 
 * 4. Show info notification:
 *    notifyInfo('Processing your request...');
 * 
 * 5. Show warning notification:
 *    notifyWarning('This action cannot be undone');
 */

// Global notification instance holder
let notificationInstance: ReturnType<typeof App.useApp>['notification'] | null = null;

/**
 * Initialize notification instance
 * This should be called from a component that has access to App context
 */
export const initNotifications = (instance: ReturnType<typeof App.useApp>['notification']) => {
  notificationInstance = instance;
};

/**
 * Get notification instance
 * Falls back to static API if not initialized (for backwards compatibility)
 */
const getNotificationApi = () => {
  if (notificationInstance) {
    return notificationInstance;
  }
  // Fallback to static API
  return staticNotification;
};

// Default notification configuration
const DEFAULT_CONFIG = {
  placement: 'topRight' as const,
  top: 24,
};

// Duration constants (in seconds)
const DURATION = {
  SUCCESS: 3,
  ERROR: 5,
  INFO: 4,
  WARNING: 4,
  NETWORK_ERROR: 0, // Don't auto-close network errors
};

/**
 * Show success notification
 * Used for successful create/update/delete operations
 * Auto-closes after 3 seconds
 * 
 * @param message - Main notification message
 * @param description - Optional detailed description
 * @param options - Additional notification options
 */
export const notifySuccess = (
  message: string,
  description?: string,
  options?: Partial<ArgsProps>
) => {
  const api = getNotificationApi();
  api.success({
    message,
    description,
    duration: DURATION.SUCCESS,
    placement: DEFAULT_CONFIG.placement,
    ...options,
  });
};

/**
 * Show error notification
 * Used for failed operations
 * Auto-closes after 5 seconds
 * 
 * @param error - Error object, string, or ApiError
 * @param customMessage - Optional custom error message
 * @param options - Additional notification options
 */
export const notifyError = (
  error: ApiError | Error | string | unknown,
  customMessage?: string,
  options?: Partial<ArgsProps>
) => {
  let message = 'An error occurred';
  let description = 'Please try again later.';

  if (isApiError(error)) {
    message = customMessage || error.message || 'An error occurred';
    
    // If there are validation errors, show them
    if (error.errors && Object.keys(error.errors).length > 0) {
      const errorMessages = Object.entries(error.errors)
        .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
        .join('\n');
      description = errorMessages;
    } else {
      description = getErrorDescription(error.statusCode);
    }
  } else if (error instanceof Error) {
    message = customMessage || 'An unexpected error occurred';
    description = error.message;
  } else if (typeof error === 'string') {
    message = customMessage || 'An error occurred';
    description = error;
  }

  const api = getNotificationApi();
  api.error({
    message,
    description,
    duration: DURATION.ERROR,
    placement: DEFAULT_CONFIG.placement,
    ...options,
  });
};

/**
 * Show info notification
 * Used for informational messages
 * Auto-closes after 4 seconds
 * 
 * @param message - Main notification message
 * @param description - Optional detailed description
 * @param options - Additional notification options
 */
export const notifyInfo = (
  message: string,
  description?: string,
  options?: Partial<ArgsProps>
) => {
  const api = getNotificationApi();
  api.info({
    message,
    description,
    duration: DURATION.INFO,
    placement: DEFAULT_CONFIG.placement,
    ...options,
  });
};

/**
 * Show warning notification
 * Used for warning messages
 * Auto-closes after 4 seconds
 * 
 * @param message - Main notification message
 * @param description - Optional detailed description
 * @param options - Additional notification options
 */
export const notifyWarning = (
  message: string,
  description?: string,
  options?: Partial<ArgsProps>
) => {
  const api = getNotificationApi();
  api.warning({
    message,
    description,
    duration: DURATION.WARNING,
    placement: DEFAULT_CONFIG.placement,
    ...options,
  });
};

/**
 * Show network error notification with retry button
 * Does not auto-close to allow user to retry
 * 
 * @param _onRetry - Optional callback function to retry the failed operation (reserved for future use)
 * @param options - Additional notification options
 */
export const notifyNetworkError = (
  _onRetry?: () => void,
  options?: Partial<ArgsProps>
) => {
  const key = 'network-error';
  const api = getNotificationApi();
  
  api.error({
    key,
    message: 'Network Error',
    description: 'Unable to connect to the server. Please check your internet connection.',
    duration: DURATION.NETWORK_ERROR,
    placement: DEFAULT_CONFIG.placement,
    ...options,
  });
  
  // Note: The retry button functionality is handled by the API client interceptor
  // Ant Design's notification btn prop requires React elements which are not
  // easily created in a pure TypeScript utility file without JSX
};

/**
 * Close a specific notification by key
 * 
 * @param key - Notification key
 */
export const closeNotification = (key: string) => {
  const api = getNotificationApi();
  api.destroy(key);
};

/**
 * Close all notifications
 */
export const closeAllNotifications = () => {
  const api = getNotificationApi();
  api.destroy();
};

// Helper functions

/**
 * Type guard to check if error is an ApiError
 */
const isApiError = (error: unknown): error is ApiError => {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    'statusCode' in error
  );
};

/**
 * Get user-friendly error description based on status code
 */
const getErrorDescription = (statusCode: number): string => {
  switch (statusCode) {
    case 400:
      return 'The request contains invalid data. Please check your input and try again.';
    case 401:
      return 'Your session has expired. Please log in again.';
    case 403:
      return 'You do not have permission to perform this action.';
    case 404:
      return 'The requested resource was not found.';
    case 409:
      return 'This action conflicts with existing data.';
    case 422:
      return 'The data provided cannot be processed.';
    case 429:
      return 'Too many requests. Please wait a moment and try again.';
    case 500:
      return 'An unexpected error occurred on the server. Please try again later.';
    case 502:
      return 'The server is temporarily unavailable. Please try again later.';
    case 503:
      return 'The service is temporarily unavailable. Please try again later.';
    default:
      return 'An unexpected error occurred. Please try again.';
  }
};

// Export notification types for convenience
export type { ArgsProps as NotificationOptions };
